import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import ConfirmProductCreation from "../forms/ConfirmProductCreation";
import CreateProductForm from "../forms/CreateProductForm";
import ImagePicker from "../forms/ImagePicker";
import API from "../../api";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  hideUpdateProductModal,
  listProducts,
} from "../../actions/productActions";

const TOTAL_STEPS = 3;

const stepDescription = [
  "Cuéntanos sobre tu producto",
  "Sube una imagen de tu producto",
  "Confirma los datos",
];

const EditProductModal = ({ isOpen }) => {
  const isVisible = useSelector((state) => state.productUpdate.updateModal);
  const savedData = useSelector((state) => state.productUpdate.product);
  const dispatch = useDispatch();
  const [productData, setProductData] = useState({
    name: savedData?.name || "",
    price: savedData?.price || "",
    description: savedData?.description || "",
    store: savedData?.stores ? savedData?.stores[0]?._id : "",
    image: savedData?.images ? savedData?.images[0] : "",
    stock: savedData?.stock || "",
  });
  const [image, setImage] = useState(null);
  const [rawImage, setRawImage] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [loading, setLoading] = useState(false);

  const onClose = () => {
    dispatch(hideUpdateProductModal());
  };

  const cancelar = () => {
    setProductData({
      name: "",
      price: "",
      description: "",
      category: "",
      store: "",
      image: "",
    });
    setImage(null);
    setRawImage(null);
    setCurrentStep(1);
    onClose();
  };

  const getHasChanged = () => {
    return (
      productData.name !== savedData.name ||
      productData.price !== savedData.price ||
      productData.description !== savedData.description ||
      productData.store !== savedData.stores[0]?._id ||
      productData.stock !== savedData.stock ||
      rawImage !== null
    );
  };

  const deletePreviousImage = async () => {
    const deletedImage = await deleteImagesFromS3(savedData.imagesDeleteIds);
    return deletedImage;
  };

  const handleUpdateProduct = async () => {
    if (!getHasChanged()) {
      toast.error("No se han realizado cambios", {
        icon: "👏",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });
      return;
    }

    setLoading(true);
    let urls = {};
    let tempKey = null;

    try {
      if (rawImage) {
        const { image } = await uploadImagesToS3({ image: rawImage });
        if (!image.location.secure_url) throw new Error({ message: "Error al subir las imágenes" });

        urls = {
          image: image.location.secure_url,
        };
        tempKey = image.location.public_id;

        const deletedImage = await deletePreviousImage();
        if (!deletedImage) throw new Error({ message: "Error al eliminar la imagen anterior" });
      }


      const product = {
        ...productData,
        stores: [productData.store],
        images: [urls.image || savedData.images[0]],
        imagesDeleteIds: [tempKey]
      };

      await API.put(
        `/products?productId=${savedData._id}`,
        product
      );
      setLoading(false);
      toast.success("Producto actualizado con éxito", {
        icon: "👏",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });

      dispatch(listProducts());
      cancelar();
    } catch (err) {
      console.log({ err });
      toast.error("Error al actualizar producto", {
        icon: "😰",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });
      await deleteImagesFromS3([tempKey]);
      setLoading(false);
    }
  };

  const deleteImagesFromS3 = (keys) => {
    return Promise.all(
      keys.map(async (key) => {
        await API.delete(`/uploads`, { data: { id: key } });
      })
    );
  };

  const uploadImagesToS3 = async ({ image }) => {
    const formData = new FormData();
    formData.append("file", image);

    try {
      const imageRes = await API.post("/uploads", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return {
        image: imageRes.data,
      };
    } catch (err) {
      console.log({ err });
      throw new Error({ message: "Error al subir las imágenes" });
    }
  };

  return (
    <Modal isOpen={isVisible} onClose={cancelar} size="lg"
        closeOnEsc={false}
        closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="2xl">Crear Producto</Text>
          <Text fontSize="md" fontWeight="light">
            {currentStep}/{TOTAL_STEPS} {stepDescription[currentStep - 1]}
          </Text>
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          {currentStep === 1 && (
            <CreateProductForm
              savedData={savedData}
              productData={productData}
              setProductData={setProductData}
              nextStep={() => setCurrentStep(2)}
            />
          )}
          {currentStep === 2 && (
            <ImagePicker
              edit
              image={
                !image ? (savedData.images ? savedData.images[0] : "") : image
              }
              rawImage={rawImage}
              setRawImage={setRawImage}
              setImage={setImage}
              nextStep={() => setCurrentStep(3)}
              prevStep={() => setCurrentStep(1)}
            />
          )}
          {currentStep === 3 && (
            <ConfirmProductCreation
              product={{
                ...productData,
                image: !image
                  ? savedData.images
                    ? savedData.images[0]
                    : ""
                  : image,
              }}
              onConfirm={handleUpdateProduct}
              onCancel={cancelar}
              loading={loading}
              edit
              savedData={savedData}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditProductModal;
